import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useShop } from "@/store/ShopContext";
import { useAuth } from "@/store/AuthContext";
import { supabase } from "@/lib/supabase";
import { currencySymbol, formatPrice } from "@/pages/home/components/ProductCard";

interface FormState {
  email: string;
  name: string;
  country: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

const initialForm: FormState = {
  email: "",
  name: "",
  country: "Brasil",
  address: "",
  city: "",
  state: "",
  zip: "",
};

const FLAT_SHIPPING = 0; // frete grátis para piloto

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartSubtotal, clearCart, showToast } = useShop();
  const { user } = useAuth();

  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>();

  useEffect(() => {
    if (!user) return;
    setForm((f) => {
      const next = { ...f };
      if (!f.email) next.email = user.email ?? "";
      const fullName = (user.user_metadata?.full_name as string | undefined) || "";
      if (fullName && !f.name) next.name = fullName;
      return next;
    });
  }, [user]);

  const currency = cart[0]?.currency ?? "BRL";
  const symbol = currencySymbol(currency);

  const shippingPrice = cart.length === 0 ? 0 : FLAT_SHIPPING;
  const total = cartSubtotal + shippingPrice;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email)) next.email = "Digite um e-mail válido";
    if (!form.name.trim()) next.name = "Obrigatório";
    if (!form.address.trim()) next.address = "Obrigatório";
    if (!form.city.trim()) next.city = "Obrigatório";
    if (!form.state.trim()) next.state = "Obrigatório";
    if (!form.zip.trim()) next.zip = "Obrigatório";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0) return;
    if (!validate()) return;
    setSubmitting(true);

    try {
      const recipient = {
        name: form.name.trim(),
        email: form.email.trim(),
        country: form.country.trim(),
        state: form.state.trim(),
        city: form.city.trim(),
        zip: form.zip.trim(),
        address: form.address.trim(),
      };

      const { data: header, error: headerErr } = await supabase
        .from("order_headers")
        .insert({
          currency,
          payment_provider: "manual",
          status: "pending_payment",
          subtotal_items: Number(cartSubtotal.toFixed(2)),
          shipping_total: Number(shippingPrice.toFixed(2)),
          customer_id: user?.id ?? null,
          recipient,
        })
        .select("id")
        .single();

      if (headerErr) throw headerErr;
      const orderId = header.id as number;

      const itemsPayload = cart.map((line) => ({
        order_id: orderId,
        product_id: String(line.productId),
        product_name: line.skuLabel
          ? `${line.productName} — ${line.skuLabel}`
          : line.productName,
        sku_id: line.skuId != null ? String(line.skuId) : null,
        sku_label: line.skuLabel,
        quantity: line.quantity,
        unit_price: Number(line.originalPrice.toFixed(2)),
        final_price: Number(line.unitPrice.toFixed(2)),
        subtotal: Number((line.unitPrice * line.quantity).toFixed(2)),
      }));

      const { error: itemsErr } = await supabase.from("order_items").insert(itemsPayload);
      if (itemsErr) throw itemsErr;

      clearCart();
      navigate(`/order-confirmation/${orderId}`, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha ao finalizar";
      showToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="text-center max-w-md">
            <h1 className="font-heading text-foreground-950 text-2xl md:text-3xl mb-6">
              Seu carrinho está vazio
            </h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Continuar comprando
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const labelCls = "block text-[12px] uppercase tracking-[0.12em] text-foreground-600 mb-1.5";
  const inputBase =
    "w-full h-11 px-3 rounded-md bg-background-50 border text-[14px] text-foreground-950 placeholder:text-foreground-400 focus:outline-none transition-colors";
  const inputOk = "border-background-300 focus:border-foreground-950";
  const inputBad = "border-primary-500 focus:border-primary-600";

  const cs = (bad?: string) => `${inputBase} ${bad ? inputBad : inputOk}`;

  const handleAutofill = async () => {
    setAutofilling(true);
    try {
      const { data, error } = await supabase
        .from("demo_addresses")
        .select("full_name, email, address_line, city, state, zip, country");
      if (error) throw error;
      if (!data || data.length === 0) {
        showToast("Nenhum endereço de demonstração disponível");
        return;
      }
      const pick = data[Math.floor(Math.random() * data.length)];
      setForm((f) => ({
        ...f,
        email: pick.email as string,
        name: pick.full_name as string,
        address: pick.address_line as string,
        city: pick.city as string,
        state: pick.state as string,
        zip: pick.zip as string,
        country: (pick.country as string) || f.country,
      }));
      setErrors({});
      showToast(`Preenchido com o endereço de ${pick.full_name}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao carregar endereço";
      showToast(msg);
    } finally {
      setAutofilling(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="max-w-[1100px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-4">
          <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl">
            Finalizar compra
          </h1>
        </section>

        <form onSubmit={onSubmit} className="max-w-[1100px] mx-auto px-4 md:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            {/* Left: form */}
            <div className="lg:col-span-7 space-y-8">
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-dashed border-background-300 bg-background-100/60">
                <div className="min-w-0">
                  <p className="text-[13px] text-foreground-950 font-medium flex items-center gap-1.5">
                    <i className="ri-flashlight-line text-primary-500"></i>
                    Preenchimento rápido (demonstração)
                  </p>
                  <p className="text-[12px] text-foreground-600 mt-0.5 truncate">
                    Preenche com um endereço de teste para simular o checkout.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAutofill}
                  disabled={autofilling}
                  className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[12.5px] font-medium transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {autofilling ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Carregando
                    </>
                  ) : (
                    <>
                      <i className="ri-shuffle-line"></i>
                      Auto-preencher
                    </>
                  )}
                </button>
              </div>

              <section>
                <h2 className="text-[13px] uppercase tracking-[0.16em] text-foreground-950 font-medium mb-4">
                  Contato
                </h2>
                <div>
                  <label className={labelCls}>E-mail</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={cs(errors.email)}
                    autoComplete="email"
                  />
                </div>
              </section>

              <section>
                <h2 className="text-[13px] uppercase tracking-[0.16em] text-foreground-950 font-medium mb-4">
                  Entrega
                </h2>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Nome completo</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className={cs(errors.name)}
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Endereço</label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                      className={cs(errors.address)}
                      autoComplete="street-address"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Cidade</label>
                      <input
                        type="text"
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        className={cs(errors.city)}
                        autoComplete="address-level2"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Estado</label>
                      <input
                        type="text"
                        value={form.state}
                        onChange={(e) => update("state", e.target.value)}
                        className={cs(errors.state)}
                        autoComplete="address-level1"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>CEP</label>
                      <input
                        type="text"
                        value={form.zip}
                        onChange={(e) => update("zip", e.target.value)}
                        className={cs(errors.zip)}
                        autoComplete="postal-code"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>País</label>
                    <select
                      value={form.country}
                      onChange={(e) => update("country", e.target.value)}
                      className={cs()}
                    >
                      {["Brasil"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

            </div>

            {/* Right: order summary */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-[13px] uppercase tracking-[0.16em] text-foreground-950 font-medium mb-4">
                  Resumo do pedido
                </h2>

                <ul className="divide-y divide-background-200/70 border-y border-background-200/70 mb-5">
                  {cart.map((line) => {
                    const lineSym = currencySymbol(line.currency);
                    return (
                      <li key={line.id} className="py-3 flex gap-3">
                        <div className="shrink-0 w-14 h-14 rounded-md overflow-hidden bg-background-100">
                          {line.image && (
                            <img
                              src={line.image}
                              alt={line.productName}
                              className="w-full h-full object-cover object-top"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13.5px] text-foreground-950 leading-snug line-clamp-2">
                            {line.productName}
                          </p>
                          {line.skuLabel && (
                            <p className="text-[12px] text-foreground-600 mt-0.5">
                              {line.skuLabel}
                            </p>
                          )}
                          <p className="text-[12px] text-foreground-600 mt-0.5">
                            Qtd {line.quantity}
                          </p>
                        </div>
                        <div className="text-right text-[13.5px] text-foreground-950 whitespace-nowrap">
                          {lineSym}{formatPrice(line.unitPrice * line.quantity)}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <dl className="space-y-2 text-[13.5px] mb-5">
                  <div className="flex justify-between text-foreground-700">
                    <dt>Subtotal</dt>
                    <dd className="text-foreground-950">
                      {symbol}{formatPrice(cartSubtotal)}
                    </dd>
                  </div>
                  <div className="flex justify-between text-foreground-700">
                    <dt>Frete</dt>
                    <dd className="text-foreground-950">
                      {shippingPrice === 0 ? "Grátis" : `${symbol}${formatPrice(shippingPrice)}`}
                    </dd>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-background-200/70">
                    <dt className="text-[14px] text-foreground-950 font-medium">Total</dt>
                    <dd className="font-heading text-foreground-950 text-xl">
                      {symbol}{formatPrice(total)}
                    </dd>
                  </div>
                </dl>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap inline-flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Finalizando…
                    </>
                  ) : (
                    <>Finalizar compra — {symbol}{formatPrice(total)}</>
                  )}
                </button>
              </div>
            </aside>
          </div>
        </form>
      </main>

      <Footer />
    </div>
  );
}